/**
 * Localized "Ask {name}" templates for the compact launcher pill.
 *
 * A standalone table rather than rows in the i18n packs, deliberately: the
 * compact label is resolved in InstanceContext BEFORE the chat i18n runtime is
 * relevant (it ships to the parent-page bootstrap, which has no i18n at all),
 * and the packs' loading must never gate the launcher's first paint.
 *
 * Per-instance overrides still win — a `compact_label` row in
 * partner_embedded_instance_strings beats this table (same mechanism as
 * `launcher_label`), and `theme.launcher.compact.label` pins a fixed string.
 * This is only the sensible default so most instances configure nothing.
 *
 * CJK entries deliberately do NOT romanize the verb pattern: 「〜に質問」/
 * 「〜에게 물어보기」/「问问〜」 is how an ask-affordance is actually labelled,
 * not a translation of the English word order.
 */
const ASK_TEMPLATES: Record<string, string> = {
  en: 'Ask {name}',
  ja: '{name}に質問',
  ko: '{name}에게 물어보기',
  'zh-hans': '问问{name}',
  'zh-hant': '問問{name}',
  zh: '问问{name}',
  es: 'Pregunta a {name}',
  fr: 'Demandez à {name}',
  de: 'Frag {name}',
  pt: 'Pergunte a {name}',
  it: 'Chiedi a {name}',
  haw: 'E nīnau iā {name}',
};

/**
 * "Ask Leilani" in the visitor's language. Locale matching mirrors
 * pickLocalizedString: exact tag first (zh-Hant), then the bare language
 * (ja-JP → ja), then English.
 */
export function askLabelFor(locale: string, name: string): string {
  const norm = (locale || 'en').replace('_', '-').toLowerCase();
  const template =
    ASK_TEMPLATES[norm] ??
    ASK_TEMPLATES[norm.split('-')[0]] ??
    ASK_TEMPLATES.en;
  return template.replace('{name}', name);
}
